import forge from 'apis/forge';
import { IconType } from 'components/Icon';
import DsConversation from 'components/Panel/Chat/Conversation/DsConversation';
import { NEW_CHAT_TITLE } from 'dragonmouse-shared/constants';
import { ChatStatus } from 'dragonmouse-shared/interfaces';
import { MenuItemAction } from 'menu/MenuItem';
import { useEffect, useMemo, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectActiveDesignSystem, selectDsChatRefs } from 'redux/db/reducers';
import { setActiveDsChatId } from 'redux/mode/actions';
import { RootReducer } from 'redux/reducers';
import { v4 as uuid } from 'uuid';
import { Header } from '../Header';
import * as Base from '../styles';

const DsChatPanel = () => {
	const dispatch = useDispatch();

	const ref = useRef() as React.MutableRefObject<HTMLDivElement>;

	const { dsChatRefs, activeDsChatId, activeDesignSystem } = useSelector((r: RootReducer) => {
		return {
			dsChatRefs: selectDsChatRefs(r.db),
			activeDsChatId: r.mode.activeDsChatId,
			activeDesignSystem: selectActiveDesignSystem(r.db, r.mode.activeDesignSystemId),
		};
	}, shallowEqual);

	// Chat refs are already sorted by updatedAt DESC from the backend
	const sortedChatRefs = dsChatRefs;

	const moreMenuItems: MenuItemAction[] = useMemo(() => {
		if (!activeDsChatId) return [];
		return [
			// Add DS-specific menu items here
		];
	}, [activeDsChatId]);

	useEffect(() => {
		if (!activeDsChatId || !sortedChatRefs.some((ref) => ref.id === activeDsChatId)) {
			const firstChatRef = sortedChatRefs.length > 0 ? sortedChatRefs[0] : null;
			if (firstChatRef) {
				dispatch(setActiveDsChatId(firstChatRef.id));
			}
		}
	}, [sortedChatRefs, activeDsChatId, dispatch]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onNewChat = async () => {
		if (!activeDesignSystem) return;

		const chatId = uuid();
		const oldActiveDsChatId = activeDsChatId;

		try {
			await forge.createDsChat(
				{
					id: chatId,
					designSystemId: activeDesignSystem.id,
					title: NEW_CHAT_TITLE,
					metadata: { status: ChatStatus.PROCESSING, statusMessage: 'Initializing chat...' },
				},
				true
			);
		} catch (e) {
			dispatch(setActiveDsChatId(oldActiveDsChatId));
			throw e;
		}
		// intentionally setting this after the API call, because otherwise the poll cache may
		// try to send over a chat that doesn't exist (i ran into a timing issue that caused
		// forge to throw an error).
		// However now the UI doesn't optimistifclly update to show the new chat has been selected
		// I think the right move is something like this:
		//    - before we call forge, set activeDsChatId to null
		//    - add some code to show a spinner when active DS Chat is loading
		//    - and keep the code here below to set it to chatId after call succeeds
		// This should work because the chat will show a spinner while the new chat is being created
		// But I'm not gonna write this until I know it's an issue - ss
		dispatch(setActiveDsChatId(chatId));
	};

	return (
		<Base.Base ref={ref}>
			<Header
				title="Design System Chat"
				onNewChat={onNewChat}
				chats={sortedChatRefs}
				activeChatId={activeDsChatId}
				onSelectChat={(id: string) => dispatch(setActiveDsChatId(id))}
				moreMenuItems={moreMenuItems}
				showTitle={true}
				showHistory={false}
				newChatButtonTooltip="Reset chat context and history"
				newChatButtonIcon={IconType.REFRESH}
			/>
			{activeDsChatId && (
				<Base.Conversation>
					<DsConversation />
				</Base.Conversation>
			)}
		</Base.Base>
	);
};

export default DsChatPanel;
